import {ColumnType, ColumnZoomType} from 'priority-web-sdk/enums';
import {Filter} from '../../stores/Filter';
import {Column} from '../../../Form/serializers/column';
import {Form} from '../../../Form/stores/Form';
import {Operator} from '../../consts/operators';
import * as bool from './bool';
import * as text from './text';
import * as range from './range';
import * as date from './date';

export interface QueryValue {
    /** The field to filter by */
    field: string;
    /** Indicates the from value to filter */
    fromval?: string;
    /** Indicates the to/until value to filter */
    toval?: string;
    /** Array of multiple values to filter */
    values?: string[];
    FromValues?: any[];
    /** Filter operator. May be one of: =, <>, >=, <=, >, < */
    op: string;
    /** Define if should sort rows by this query.
     * it could be 0 that stands for no sort
     * or any positive number that stands for sortIndex
     */
    sort: string;
    /** Define sort rows by desc. could be 0 or 1 */
    isdesc: string;
    tables?: unknown;
}

export function serialize(column: Column, queryValue: QueryValue, index): Partial<Filter> {
    const {field, sort, isdesc} = queryValue;
    const filter = {
        field,
        issort: sort && sort !== '0',
        isdesc: isdesc === '1',
        sortIndex: index,
        column
    };
    switch (column.type) {
        case ColumnType.Bool:
            return {
                ...filter,
                ...bool.serialize(queryValue)
            };
        case ColumnType.Date:
            return {
                ...filter,
                ...date.serialize(queryValue)
            };
        case ColumnType.Time:
        case ColumnType.Duration:
            return {
                ...filter,
                ...range.serialize(queryValue)
            };
        default:
            switch (column.zoom) {
                case ColumnZoomType.Search:
                case ColumnZoomType.Choose:
                    return {
                        ...filter,
                        ...text.serialize(queryValue)
                    };
                default:
                    switch (column.type) {
                        case ColumnType.Number:
                            return {
                                ...filter,
                                ...range.serialize(queryValue)
                            };
                        default:
                            //the abscence of the operator is interpreted
                            //as sign of the between operator
                            if (!queryValue.op) {
                                return {
                                    ...filter,
                                    ...range.serialize(queryValue)
                                };
                            }
                            return {
                                ...filter,
                                ...text.serialize(queryValue)
                            };
                    }
            }
    }
}

export function deserialize(filter: Filter, form: Form, flatValues: boolean = false): QueryValue {
    const {column, field, issort, isdesc, sortIndex} = filter;
    const format = column.format;
    const fieldNum =
        form.getColumnByNumber(field.toString()).name === column.name
            ? field
            : form.getColumnByName(column.name.toString())?.field;
    const queryValue = {
        field: fieldNum,
        sort: issort ? (sortIndex + 1).toString() : '0',
        isdesc: isdesc ? '1' : '0',
        op: ''
    };
    // when filter is empty - we send an empty string in operator
    // so only the sort will take place
    // except for boolean fields - that empty value means <> 'Y'
    if (
        filter.isEmpty &&
        column.type !== ColumnType.Bool &&
        filter.operator !== Operator.Empty &&
        filter.operator !== Operator.NotEmpty
    ) {
        return queryValue;
    }
    switch (column.type) {
        case ColumnType.Bool:
            return {
                ...queryValue,
                ...bool.deserialize(filter as bool.filterType)
            };
        case ColumnType.Date:
            return {
                ...queryValue,
                ...date.deserialize(filter as date.filterType, format)
            };
        case ColumnType.Time:
        case ColumnType.Duration:
            return {
                ...queryValue,
                ...range.deserialize(filter as range.filterType, format)
            };
        default:
            switch (column.zoom) {
                case ColumnZoomType.Search:
                case ColumnZoomType.Choose:
                    const vals = flatValues
                        ? {values: Array.from(filter.values).sort()}
                        : text.deserialize(filter as text.filterType);
                    return {
                        ...queryValue,
                        ...vals
                    };
                default:
                    switch (column.type) {
                        case ColumnType.Number:
                            return {
                                ...queryValue,
                                ...range.deserialize(filter as range.filterType)
                            };
                        default:
                            if (filter.operator === Operator.Between) {
                                return {
                                    ...queryValue,
                                    ...range.deserialize({
                                        columnType: filter.column.type,
                                        ...filter
                                    } as range.filterType)
                                };
                            }
                            const values = flatValues
                                ? {values: Array.from(filter.values).sort()}
                                : text.deserialize(filter as text.filterType);
                            return {
                                ...queryValue,
                                ...values
                            };
                    }
            }
    }
}
